import { z } from 'zod';
import { SignalLinkedFindingPreviewSchema } from '@terra/shared-contracts-findings';
import {
  PaginationQueryParamsSchema,
  OptionalBooleanQueryParamSchema,
  OptionalStringArrayQueryParamSchema,
  createConfidenceFilterQueryParamSchema,
  LifecycleStateFilterSchema,
} from '@terra/shared-dto-types';
import {
  ActivityOperatorSchema,
  ActivityPhaseSchema,
  ActivityTypeSchema,
  ApplicationProtocolSchema,
  DnsRrTypeSchema,
  DnsSourceTypeSchema,
  DomainEvidenceSchema,
  ExternalNetworkProfileSchema,
  FindingSeveritySchema,
  FindingStatusSchema,
  ConfirmedVulnerabilityFindingStateSchema,
  GatewayProviderCategorySchema,
  GatewaySourceTypeSchema,
  GuardrailActionSchema,
  GuardrailCategorySchema,
  InstructionCategorySchema,
  TestingInstructionApplyModeSchema,
  TestingInstructionPipelineSchema,
  NetblockEvidenceSchema,
  NetblockSourceTypeSchema,
  ScopeSchema,
  SignalAssetTypeSchema,
  SignalCategorySchema,
  SignalOriginSchema,
  SignalStageSchema,
  SignalStatusSchema,
  SoftwareObservationSourceTypeSchema,
  SoftwareOwnershipSchema,
  SoftwareTypeSchema,
  WebAppCandidateVerdictSchema,
  WebAppDraftStrategySchema,
  SurfaceBusinessProfileInputSchema,
  SurfaceDivisionSchema,
  SurfaceSubsidiarySchema,
  TransportProtocolSchema,
  VhostBindingSourceSchema,
  EXTERNAL_NETWORK_ENTITY_KINDS,
  ExternalNetworkEntityKindSchema,
} from '@terra/shared-schemas-orchestrator';
import { FindingListEnrichedDtoSchema } from '@terra/shared-contracts-findings';
import {
  EGRESS_STRATEGY_VALUES,
  DEFAULT_EGRESS_STRATEGY,
} from './external-network.constants';
import {
  AssetDetectionMetricsSchema,
  ExternalNetworkDiscoverySourceTypeSchema,
  HostDtoSchema,
  HostGeoSchema,
  NetworkServiceDtoSchema,
} from './external-network-asset.schema';
import { ScanConfigOverridesSchema } from './scan-config.schema';
import { StorageServicePublicAccessSchema } from './storage-service.schema';

export {
  ScopeSchema,
  DnsRrTypeSchema,
  DnsSourceTypeSchema,
  NetblockSourceTypeSchema,
  ActivityPhaseSchema,
  ActivityTypeSchema,
  ActivityOperatorSchema,
  TransportProtocolSchema,
  ApplicationProtocolSchema,
  SoftwareTypeSchema,
  SoftwareOwnershipSchema,
  SoftwareObservationSourceTypeSchema,
  WebAppCandidateVerdictSchema,
  WebAppDraftStrategySchema,
  AssetDetectionMetricsSchema,
  ExternalNetworkDiscoverySourceTypeSchema,
  HostDtoSchema,
  HostGeoSchema,
  NetworkServiceDtoSchema,
};

export const EgressStrategySchema = z.enum(EGRESS_STRATEGY_VALUES);

export const ScanProfileTierSchema = z.enum([
  'STEALTH',
  'BALANCED',
  'AGGRESSIVE',
]);

export const ScanProfileSchema = ScanProfileTierSchema;

export type ScanProfileTier = z.infer<typeof ScanProfileTierSchema>;
export type ScanProfile = ScanProfileTier;

export const ExternalNetworkDeletionStatusSchema = z.enum([
  'PENDING',
  'RUNNING',
  'FAILED',
]);

export const ExternalNetworkScanStatusSchema = z.enum([
  'IDLE',
  'DISCOVERING',
  'SCANNING',
  'ANALYZING',
  'DIFFING',
  'COMPLETED',
  'FAILED',
  'STOPPED',
]);

export const ExternalNetworkScanTypeSchema = z.enum([
  'RECON',
  'DISCOVERY',
  'CVE_RETEST',
  'VULNERABILITY',
]);
export const TriggerScanTypeSchema = z.enum([
  'RECON',
  'DISCOVERY',
  'VULNERABILITY',
  'FULL',
]);
export const ScanChainStepSchema = ExternalNetworkScanTypeSchema.extract([
  'DISCOVERY',
  'VULNERABILITY',
]);
export const ScanChainRemainingSchema = z.array(ScanChainStepSchema);

export { EXTERNAL_NETWORK_ENTITY_KINDS, ExternalNetworkEntityKindSchema };

export const SignalTargetAssetTypeSchema = z.enum([
  'DOMAIN',
  'HOST',
  'NETWORK_SERVICE',
  'SOFTWARE',
  'STORAGE_SERVICE',
]);

export const SIGNAL_TARGET_ASSET_KINDS = [
  'DOMAIN',
  'HOST',
  'NETWORK_SERVICE',
  'SOFTWARE',
  'STORAGE_SERVICE',
] as const;

export type SignalTargetAssetKind = (typeof SIGNAL_TARGET_ASSET_KINDS)[number];

export const SignalTargetAssetKindSchema = z.enum([
  ...SIGNAL_TARGET_ASSET_KINDS,
]);

export const ExternalNetworkAttackScopeSchema = z.enum([
  'ENTITY_ONLY',
  'RELATED_ENTITIES',
  'FULL_SURFACE',
]);

export type ExternalNetworkAttackScopeType = z.infer<
  typeof ExternalNetworkAttackScopeSchema
>;

export const AutomationScheduleSchema = z.object({
  daysOfWeek: z.array(z.number().min(0).max(6)),
  startHour: z.number().min(0).max(23),
  endHour: z.number().min(0).max(24),
  timezone: z.string().nullable().optional(),
});

const normalizeAssetEntries = (entries: string[]): string[] =>
  entries.map((entry) => entry.trim()).filter(Boolean);
const DomainEntriesSchema = z
  .array(z.string())
  .transform(normalizeAssetEntries);
const NetblockEntriesSchema = z
  .array(z.string())
  .transform(normalizeAssetEntries);

// ==================== Surface ====================
export const ExternalNetworkSchema = z.object({
  id: z.string().describe('Unique identifier'),
  tenantId: z.string().nullable().describe('Tenant identifier'),
  name: z.string().describe('Surface name'),
  organizationName: z
    .string()
    .nullable()
    .optional()
    .describe('Organization name for reverse WHOIS seed expansion'),
  scanProfileId: z.string().describe('Assigned scan profile identifier'),
  scanProfileName: z
    .string()
    .optional()
    .describe('Denormalized scan profile display name'),
  smokeTestEnabled: z
    .boolean()
    .describe('Whether automated Neo execution is enabled for surface signals'),
  detectWebAppsEnabled: z
    .boolean()
    .nullish()
    .transform((value) => value ?? false)
    .describe(
      'Whether discovery may auto-create draft web applications from this surface',
    ),
  webAppDraftStrategy: WebAppDraftStrategySchema.nullish()
    .transform((value) => value ?? 'PER_ORIGIN')
    .describe('How discovered web hosts are grouped into draft applications'),
  egressStrategy: EgressStrategySchema.describe(
    'Egress routing strategy: DIRECT, CONNECTOR (Tailscale), or ROTATING_PROXY (Bright Data)',
  ),
  internalAccessNetworkId: z
    .string()
    .nullable()
    .optional()
    .describe('Internal network bound for connector egress'),
  dnsServerIps: z
    .array(z.string())
    .nullish()
    .transform((value) => value ?? [])
    .describe('Internal DNS resolvers used when egress is CONNECTOR'),
  schedule: AutomationScheduleSchema.nullable().describe(
    'Scheduled discovery scan window',
  ),
  executionSchedule: AutomationScheduleSchema.nullable().describe(
    'Automated Neo execution schedule window',
  ),
  isEnabled: z.boolean().describe('Whether automated scanning is active'),
  deletionStatus: ExternalNetworkDeletionStatusSchema.nullable()
    .optional()
    .describe('Set while an async deletion is in flight or has failed'),
  createdAt: z.coerce.date().describe('Creation timestamp'),
  updatedAt: z.coerce.date().describe('Last update timestamp'),
});

export const ConnectorReadinessReasonSchema = z.enum([
  'no_network_selected',
  'connector_not_online',
  'routes_not_approved',
  'resource_proxy_unsupported',
]);

export const ConnectorReadinessSchema = z.object({
  ready: z.boolean(),
  reason: ConnectorReadinessReasonSchema.optional(),
  message: z.string().optional(),
});

export const SubnetListItemSchema = z.object({
  id: z.string(),
  cidr: z.string(),
  version: z.number().int(),
  scope: ScopeSchema,
  sourceType: z.enum(['SYSTEM_ROOT', 'USER_PROVIDED']),
  description: z.string().nullable(),
  parentId: z.string().nullable(),
});

export const SubnetListResponseSchema = z.object({
  subnets: SubnetListItemSchema.array(),
});

export const CreateExternalNetworkSchema = z
  .object({
    name: z.string().min(1).max(100).describe('Surface name'),
    organizationName: z
      .string()
      .min(1)
      .max(200)
      .optional()
      .describe('Organization name for reverse WHOIS seed expansion'),
    domains: DomainEntriesSchema.optional()
      .default([])
      .describe('User-provided domains'),
    netblocks: NetblockEntriesSchema.optional()
      .default([])
      .describe('User-provided netblocks'),
    subnets: z
      .array(
        z.object({
          cidr: z.string().min(1),
          description: z.string().max(200).optional(),
        }),
      )
      .optional()
      .default([])
      .describe(
        'Connector subnets. Private aggregates stay out of scope; selected ranges are in-scope children.',
      ),
    scanProfileId: z
      .string()
      .optional()
      .describe('Scan profile to assign; defaults to Balanced system profile'),
    smokeTestEnabled: z.boolean().optional().default(false),
    detectWebAppsEnabled: z.boolean().optional().default(false),
    webAppDraftStrategy:
      WebAppDraftStrategySchema.optional().default('PER_ORIGIN'),
    egressStrategy: EgressStrategySchema.optional()
      .default(DEFAULT_EGRESS_STRATEGY)
      .describe(
        'Egress routing strategy: DIRECT, CONNECTOR (Tailscale), or ROTATING_PROXY (Bright Data)',
      ),
    internalAccessNetworkId: z
      .string()
      .min(1)
      .optional()
      .describe('Internal network bound for connector egress'),
    schedule: AutomationScheduleSchema.optional().describe(
      'Scheduled discovery scan window',
    ),
    executionSchedule: AutomationScheduleSchema.optional().describe(
      'Automated Neo execution schedule window',
    ),
    profile: z
      .lazy(() => UpsertSurfaceProfileSchema)
      .optional()
      .describe('Optional business profile captured during wizard creation'),
    profileResearchJobId: z
      .string()
      .optional()
      .describe('Optional in-flight business profile research job id'),
    dnsServerIps: z
      .array(z.string())
      .optional()
      .default([])
      .describe('Internal DNS resolvers required for connector egress'),
  })
  .superRefine((payload, context) => {
    const domains = payload.domains ?? [];
    const netblocks = payload.netblocks ?? [];
    const subnets = payload.subnets ?? [];

    if (payload.egressStrategy === 'CONNECTOR') {
      if (subnets.length + netblocks.length === 0) {
        context.addIssue({
          code: 'custom',
          path: ['subnets'],
          message: 'Select at least one subnet',
        });
      }

      if (!payload.internalAccessNetworkId) {
        context.addIssue({
          code: 'custom',
          path: ['internalAccessNetworkId'],
          message: 'An internal network is required for connector egress',
        });
      }

      if ((payload.dnsServerIps ?? []).length === 0) {
        context.addIssue({
          code: 'custom',
          path: ['dnsServerIps'],
          message:
            'At least one DNS server IP is required for connector egress',
        });
      }
    } else if (domains.length + netblocks.length === 0) {
      context.addIssue({
        code: 'custom',
        path: ['domains'],
        message: 'At least one domain, IP address, or CIDR range is required',
      });
    }
  });

export const UpdateExternalNetworkSchema = z
  .object({
    name: z.string().min(1).max(100).optional(),
    organizationName: z.string().min(1).max(200).nullable().optional(),
    smokeTestEnabled: z.boolean().optional(),
    detectWebAppsEnabled: z.boolean().optional(),
    webAppDraftStrategy: WebAppDraftStrategySchema.optional(),
    egressStrategy: EgressStrategySchema.optional(),
    internalAccessNetworkId: z.string().min(1).nullable().optional(),
    dnsServerIps: z.array(z.string()).optional(),
    schedule: AutomationScheduleSchema.nullable().optional(),
    executionSchedule: AutomationScheduleSchema.nullable().optional(),
    isEnabled: z.boolean().optional(),
  })
  .superRefine((payload, context) => {
    if (
      payload.egressStrategy === 'CONNECTOR' &&
      !payload.internalAccessNetworkId
    ) {
      context.addIssue({
        code: 'custom',
        path: ['internalAccessNetworkId'],
        message: 'An internal network is required for connector egress',
      });
    }
  });

export const SurfaceProfileSchema = ExternalNetworkProfileSchema.extend({
  divisions: z.array(SurfaceDivisionSchema).default([]),
  subsidiaries: z.array(SurfaceSubsidiarySchema).default([]),
  locations: z.array(z.string()).default([]),
});

export const UpsertSurfaceProfileSchema = SurfaceBusinessProfileInputSchema;

export const SurfaceProfileResearchStatusSchema = z.enum([
  'RESEARCHING',
  'COMPLETED',
  'FAILED',
]);

export const StartProfileResearchSchema = z.object({
  companyName: z.string().trim().min(1).max(200),
});

export const ProfileResearchJobPathParamsSchema = z.object({
  jobId: z
    .string()
    .min(1)
    .describe('Unique identifier of the profile research job'),
});

export const SurfaceProfilePathParamsSchema = z.object({
  surfaceId: z
    .string()
    .min(1)
    .describe('Unique identifier of the external network'),
});

export const ProfileResearchSchema = z.object({
  jobId: z.string(),
  status: SurfaceProfileResearchStatusSchema,
  profile: UpsertSurfaceProfileSchema.nullable().optional(),
  error: z.string().nullable().optional(),
});

export const SurfaceAssetSkipReasonSchema = z.enum([
  'EMPTY',
  'INVALID_FORMAT',
  'RESERVED_IP',
  'TOO_LONG',
]);

export const AddExternalNetworkDomainsSchema = z.object({
  domains: DomainEntriesSchema.optional().default([]),
});

export const AddExternalNetworkNetblocksSchema = z.object({
  netblocks: NetblockEntriesSchema.optional().default([]),
});

export const AddExternalNetworkAssetsResponseSchema = z.object({
  added: z.int().nonnegative(),
  skipped: z.array(
    z.object({
      value: z.string(),
      reason: SurfaceAssetSkipReasonSchema,
    }),
  ),
});

// ==================== Scan ====================
export const ScanStageInfoSchema = z.object({
  name: z.string(),
  status: z.string(),
  startedAt: z.coerce.date().nullable().optional(),
  completedAt: z.coerce.date().nullable().optional(),
  itemsTotal: z.number().nullable().optional(),
  itemsDone: z.number().nullable().optional(),
  itemsIncomplete: z.number().nullable().optional(),
  expectedChunks: z.number().int().nonnegative().nullable().optional(),
  completionPublishedAt: z.coerce.date().nullable().optional(),
  lastProgressAt: z.coerce.date().nullable().optional(),
});

export const ExternalNetworkScanSchema = z.object({
  id: z.string().describe('Scan identifier'),
  tenantId: z.string().nullable().describe('Tenant identifier'),
  surfaceId: z.string().describe('Parent surface identifier'),
  scanType: ExternalNetworkScanTypeSchema.describe('Type of scan run'),
  status: ExternalNetworkScanStatusSchema.describe('Current scan status'),
  currentStage: z.string().nullable().describe('Active pipeline stage'),
  stages: z.array(ScanStageInfoSchema).describe('Per-stage progress'),
  entitiesUpserted: z
    .number()
    .describe('Total entities (any type) upserted in this scan cycle'),
  newEntities: z
    .number()
    .describe('Entities discovered for the first time in this scan'),
  removedEntities: z
    .number()
    .describe('Entities no longer seen compared to previous scan'),
  startedAt: z.coerce.date().nullable().describe('Scan start time'),
  completedAt: z.coerce.date().nullable().describe('Scan completion time'),
  failureReason: z.string().nullable().describe('Failure reason if FAILED'),
  chainDiscovery: z
    .boolean()
    .optional()
    .describe('When true, recon completion starts a discovery scan'),
  createdAt: z.coerce.date().describe('Record creation timestamp'),
});

// ==================== Embedded value types ====================
export const DnsRecordSchema = z.object({
  rrType: DnsRrTypeSchema,
  ttl: z.number().nullable().optional(),
  rdata: z.string(),
});

export const NetblockWhoisSchema = z.object({
  handle: z.string().nullable().optional(),
  organization: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  netType: z.string().nullable().optional(),
  parentHandle: z.string().nullable().optional(),
  netname: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  registry: z.string().nullable().optional(),
  asn: z.string().nullable().optional(),
  registeredCidr: z.string().nullable().optional(),
  status: z.array(z.string()).default([]),
  entities: z
    .array(
      z.object({
        handle: z.string().nullable().optional(),
        roles: z.array(z.string()).default([]),
        name: z.string().nullable().optional(),
        organization: z.string().nullable().optional(),
        email: z.string().nullable().optional(),
        phone: z.string().nullable().optional(),
        country: z.string().nullable().optional(),
        address: z.string().nullable().optional(),
      }),
    )
    .default([]),
  fetchedAt: z.coerce.date().nullable().optional(),
});

const NetblockEvidenceDtoSchema = NetblockEvidenceSchema.extend({
  timestamp: z.coerce.date().nullable().optional(),
});

const DomainEvidenceDtoSchema = DomainEvidenceSchema.extend({
  timestamp: z.coerce.date().nullable().optional(),
});

const SoftwareObservationDtoSchema = z.object({
  sourceType: SoftwareObservationSourceTypeSchema,
  text: z.string(),
  timestamp: z.coerce.date().nullable().optional(),
});

// ==================== Entities ====================
export const NetBlockDtoSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  surfaceId: z.string(),
  parentId: z.string().nullable(),
  cidr: z.string(),
  version: z.int(),
  scope: ScopeSchema,
  whois: NetblockWhoisSchema.nullable(),
  sourceType: NetblockSourceTypeSchema,
  evidences: z.array(NetblockEvidenceDtoSchema),
  expansionProcessedAt: z.coerce.date().nullable().optional(),
  cloudProvider: z.string().nullable().optional(),
  cloudAccountRef: z.string().nullable().optional(),
  cloudAllocationId: z.string().nullable().optional(),
  cloudResourceKind: z.string().nullable().optional(),
  resourceKind: z.string().nullable().optional(),
  inventoryLinked: z.boolean().optional(),
  deprovisionedAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  detectionMetrics: AssetDetectionMetricsSchema.optional(),
  hostCount: z.int().nonnegative().optional(),
  effectiveScope: ScopeSchema.optional(),
  scopeInherited: z.boolean().optional(),
});

export const DomainDtoSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  surfaceId: z.string(),
  fqdn: z.string(),
  isTopLevel: z.boolean(),
  parentId: z.string().nullable(),
  dnsRecords: z.array(DnsRecordSchema),
  scope: ScopeSchema,
  sourceType: DnsSourceTypeSchema,
  evidences: z.array(DomainEvidenceDtoSchema),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  detectionMetrics: AssetDetectionMetricsSchema.optional(),
  resolvedCloudProvider: z.string().nullable().optional(),
  registrar: z.string().nullable().optional(),
  subdomainCount: z.int().nonnegative().optional(),
  effectiveScope: ScopeSchema.optional(),
  scopeInherited: z.boolean().optional(),
});

export const GatewayDtoSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  surfaceId: z.string(),
  hostname: z.string(),
  provider: z.string().nullable(),
  providerCategory: GatewayProviderCategorySchema,
  sourceType: GatewaySourceTypeSchema,
  ips: z.array(z.string()),
  resolvedDomainIds: z.array(z.string()),
  cloudProvider: z.string().nullable().optional(),
  cloudAccountRef: z.string().nullable().optional(),
  cloudResourceId: z.string().nullable().optional(),
  originHosts: z.array(z.string()).optional(),
  lastSeenAt: z.coerce.date().nullable().optional(),
  deprovisionedAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  detectionMetrics: AssetDetectionMetricsSchema.optional(),
});
export const GatewayListItemSchema = GatewayDtoSchema.extend({
  scope: ScopeSchema,
  resolvedDomainCount: z.int(),
  serviceCount: z.int(),
});
export const GatewayDetailSchema = GatewayListItemSchema.extend({
  domains: z.array(
    z.object({
      id: z.string(),
      fqdn: z.string(),
      scope: ScopeSchema,
      source: VhostBindingSourceSchema.optional(),
    }),
  ),
  services: z.array(
    z.object({
      id: z.string(),
      port: z.int(),
      transportProtocol: TransportProtocolSchema,
      applicationProtocol: ApplicationProtocolSchema,
      tlsEnabled: z.boolean(),
    }),
  ),
});

export const SoftwareDtoSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  surfaceId: z.string(),
  hostId: z.string().nullable(),
  networkServiceId: z.string().nullable(),
  domainName: z.string().nullable(),
  domainId: z.string().nullable().optional(),
  softwareType: SoftwareTypeSchema,
  ownership: SoftwareOwnershipSchema.nullable().optional(),
  vendor: z.string().optional(),
  product: z.string(),
  version: z.string().nullable(),
  observations: z.array(SoftwareObservationDtoSchema),
  hostIp: z.string().optional(),
  port: z.int().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  detectionMetrics: AssetDetectionMetricsSchema.optional(),
});

export const ActivityRunDtoSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  scanId: z.string().nullable(),
  phase: ActivityPhaseSchema,
  activityType: ActivityTypeSchema,
  targetId: z.string(),
  assetId: z.string().nullable().optional(),
  assetType: SignalAssetTypeSchema.nullable().optional(),
  attackScope: ExternalNetworkAttackScopeSchema.nullable().optional(),
  targetEntityKind: ExternalNetworkEntityKindSchema.nullable().optional(),
  summary: z.string(),
  operator: ActivityOperatorSchema,
  traceId: z.string().nullable(),
  startedAt: z.coerce.date(),
  completedAt: z.coerce.date().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
});

// ==================== List queries / responses ====================
export const ExternalNetworkListQuerySchema =
  PaginationQueryParamsSchema.extend({
    isEnabled: z
      .union([
        z.literal('true').transform(() => true as const),
        z.literal('false').transform(() => false as const),
        z.boolean(),
      ])
      .optional()
      .describe('Filter by enabled state'),
  });

export const ExternalNetworkScanListQuerySchema =
  PaginationQueryParamsSchema.extend({
    status: ExternalNetworkScanStatusSchema.optional().describe(
      'Filter by scan status',
    ),
    scanType: ExternalNetworkScanTypeSchema.optional().describe(
      'Filter by scan type',
    ),
  });

export const ExternalNetworkListResponseSchema = z.object({
  data: z.array(ExternalNetworkSchema),
  total: z.number(),
});

export const ExternalNetworkScanListResponseSchema = z.object({
  data: z.array(ExternalNetworkScanSchema),
  total: z.number(),
});

export const ActivityRunListQuerySchema = PaginationQueryParamsSchema.extend({
  scanId: z.string().optional(),
  phase: ActivityPhaseSchema.optional(),
  activityType: ActivityTypeSchema.optional(),
  operator: ActivityOperatorSchema.optional(),
  targetId: z.string().optional(),
});
export const ActivityRunListResponseSchema = z.object({
  data: z.array(ActivityRunDtoSchema),
  total: z.number(),
});

export const ExternalNetworkAssetFilterCountrySchema = z.object({
  countryCode: z.string(),
  country: z.string().nullable(),
});

export const ExternalNetworkAssetFilterOptionsSchema = z.object({
  products: z.array(z.string()),
  cveIds: z.array(z.string()),
  regions: z.array(z.string()),
  organizations: z.array(z.string()),
  countries: z.array(ExternalNetworkAssetFilterCountrySchema).default([]),
});

export const ExternalNetworkAssetBreakdownKindSchema = z.enum([
  'HOST',
  'GATEWAY',
  'NETWORK_SERVICE',
  'SOFTWARE',
  'STORAGE_SERVICE',
]);

export const ExternalNetworkAssetBreakdownSegmentSchema = z.object({
  id: z.string(),
  count: z.number(),
});

export const ExternalNetworkAssetBreakdownQuerySchema = z.object({
  kind: ExternalNetworkAssetBreakdownKindSchema,
});

export const ExternalNetworkAssetBreakdownSchema = z.object({
  segments: z.array(ExternalNetworkAssetBreakdownSegmentSchema),
});

// ==================== Stats ====================
export const FindingsCountsBySeveritySchema = z.object({
  critical: z.number(),
  high: z.number(),
  medium: z.number(),
  low: z.number(),
  info: z.number(),
});

export const ExternalNetworkStatsSchema = z.object({
  totalNetblocks: z.number().describe('Total NetBlock entities'),
  totalDomains: z.number().describe('Total Domain entities'),
  totalHosts: z.number().describe('Total Host entities'),
  totalGateways: z.number().describe('Total Gateway entities'),
  totalServices: z.number().describe('Total NetworkService entities'),
  totalSoftware: z.number().describe('Total Software entities'),
  totalStorageServices: z
    .number()
    .describe('Total StorageService entities')
    .optional(),
  netblocksInScope: z.number().describe('NetBlocks marked IN_SCOPE'),
  domainsInScope: z.number().describe('Domains marked IN_SCOPE'),
  totalSignals: z.number().describe('Total active signals on surface entities'),
  signalCountsByStage: z
    .record(SignalStageSchema, z.number())
    .describe('Signal counts grouped by stage'),
  findingsCountsBySeverity: FindingsCountsBySeveritySchema.describe(
    'Finding counts grouped by severity',
  ),
  hostCountsByCloudProvider: z
    .record(z.string(), z.number())
    .describe('Host counts grouped by cloud provider'),
  gatewayCountsByProvider: z
    .record(z.string(), z.number())
    .optional()
    .describe('Live gateway counts grouped by provider or cloud provider'),
  lastScanAt: z.coerce.date().nullable().describe('Last completed scan time'),
  lastScanStatus: ExternalNetworkScanStatusSchema.nullable().describe(
    'Status of the most recent scan',
  ),
  storageServicesPublic: z
    .number()
    .describe('StorageService entities with public access')
    .optional(),
});

export const ExternalNetworkAllStatsResponseSchema = z.record(
  z.string(),
  ExternalNetworkStatsSchema,
);

export const ExternalNetworkKpiMetricsItemSchema = z.object({
  current: z.number().describe('Current count value for the selected period'),
  previous: z
    .number()
    .describe(
      'Previous count value for the equivalent period before the selected period',
    ),
});

export const ExternalNetworkCloudSplitSchema = z.object({
  hosts: z
    .record(z.string(), z.number())
    .describe(
      'Hosts created in the selected period, grouped by cloud provider',
    ),
  storage: z
    .record(z.string(), z.number())
    .describe(
      'Live storage services created in the selected period, grouped by cloud provider',
    ),
  domains: z
    .record(z.string(), z.number())
    .describe(
      'Domains created in the selected period, attributed by resolved host cloud providers',
    ),
});

export const ExternalNetworkKpiMetricsResponseSchema = z.object({
  findings: ExternalNetworkKpiMetricsItemSchema,
  signals: ExternalNetworkKpiMetricsItemSchema,
  assets: ExternalNetworkKpiMetricsItemSchema,
  cloudSplit: ExternalNetworkCloudSplitSchema,
});

export const ExternalNetworkAssetsTrendQuerySchema = z
  .object({
    from: z
      .string()
      .optional()
      .describe('Start date for the trend period (ISO 8601 format)'),
    to: z
      .string()
      .optional()
      .describe('End date for the trend period (ISO 8601 format)'),
  })
  .superRefine((value, ctx) => {
    const fromDate = value.from ? new Date(value.from) : undefined;
    const toDate = value.to ? new Date(value.to) : undefined;

    if (value.from && fromDate && Number.isNaN(fromDate.getTime())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['from'],
        message: 'from must be a valid ISO 8601 date',
      });
    }

    if (value.to && toDate && Number.isNaN(toDate.getTime())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['to'],
        message: 'to must be a valid ISO 8601 date',
      });
    }

    if (
      fromDate &&
      toDate &&
      !Number.isNaN(fromDate.getTime()) &&
      !Number.isNaN(toDate.getTime()) &&
      fromDate.getTime() > toDate.getTime()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['from'],
        message: 'from must be before or equal to to',
      });
    }
  });

export const ExternalNetworkAssetsTrendByKindSchema = z.object({
  netblocks: z.number().int().nonnegative(),
  domains: z.number().int().nonnegative(),
  hosts: z.number().int().nonnegative(),
  gateways: z.number().int().nonnegative(),
  services: z.number().int().nonnegative(),
  software: z.number().int().nonnegative(),
  storageServices: z.number().int().nonnegative(),
});

export const ExternalNetworkAssetsTrendDataSchema = z.object({
  date: z.string().describe('Date in YYYY-MM-DD format'),
  totalAssets: z
    .number()
    .describe('Cumulative count of all EN assets detected by this date'),
  byKind: ExternalNetworkAssetsTrendByKindSchema,
});

export const ExternalNetworkSoftwareTypeCountSchema = z.object({
  softwareType: SoftwareTypeSchema,
  count: z.number().int().nonnegative(),
});

export const ExternalNetworkSoftwareTopProductSchema = z.object({
  product: z.string(),
  vendor: z.string().nullable(),
  softwareType: SoftwareTypeSchema,
  count: z.number().int().nonnegative(),
});

export const ExternalNetworkSoftwareSummarySchema = z.object({
  byType: z.array(ExternalNetworkSoftwareTypeCountSchema),
  topProducts: z.array(ExternalNetworkSoftwareTopProductSchema),
});

export const ExternalNetworkAssetsTrendResponseSchema = z.object({
  data: z.array(ExternalNetworkAssetsTrendDataSchema),
});

// ==================== Action responses ====================
export const TriggerScanBodySchema = z.object({
  scanType: TriggerScanTypeSchema.default('FULL').describe(
    'Scan to trigger: RECON, DISCOVERY, VULNERABILITY, or FULL chain',
  ),
  overrides: ScanConfigOverridesSchema.optional().describe(
    'Optional per-stage tool config overrides applied on top of the profile defaults',
  ),
});

export const TriggerScanResponseSchema = z.object({
  scanId: z.string().describe('ID of the initiated scan'),
  message: z.string(),
});

export const CveRetestJobStatusSchema = z.enum([
  'PENDING',
  'RUNNING',
  'COMPLETED',
  'FAILED',
]);

export const TriggerCveRetestBodySchema = z.object({
  cveIds: z.array(z.string().min(1)).max(50).optional(),
});

export const CveRetestJobSchema = z.object({
  id: z.string(),
  surfaceId: z.string(),
  status: CveRetestJobStatusSchema,
  scanType: z.literal('CVE_RETEST'),
  scanId: z.string().nullable(),
  cveIds: z.array(z.string()),
  matchedHostCount: z.number(),
  smokeTestTriggered: z.boolean(),
  errorMessage: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const StopScanResponseSchema = z.object({
  message: z.string(),
});

export const DeleteExternalNetworkResponseSchema = z.object({
  message: z.string(),
});

export const ScheduleExternalNetworkDeletionResponseSchema = z.object({
  surfaceId: z.string(),
  deletionStatus: ExternalNetworkDeletionStatusSchema,
});

const SURFACE_SIGNAL_CONFIDENCE_FILTER_VALUES = [
  'HIGHEST',
  'HIGH',
  'MEDIUM',
  'LOW',
  'LOWEST',
  'ALL',
] as const;

const SurfaceSignalConfidenceFilterSchema =
  createConfidenceFilterQueryParamSchema(
    SURFACE_SIGNAL_CONFIDENCE_FILTER_VALUES,
  );

const SurfaceSignalCategoryQuerySchema = OptionalStringArrayQueryParamSchema;

// ==================== Surface Signals ====================
export const ExternalNetworkSignalListQuerySchema =
  PaginationQueryParamsSchema.extend({
    category: SurfaceSignalCategoryQuerySchema.describe(
      'Filter by signal category',
    ),
    search: z
      .string()
      .optional()
      .describe('Case-insensitive contains filter on signal description'),
    entityKind: ExternalNetworkEntityKindSchema.optional().describe(
      'Filter signals by target entity kind',
    ),
    targetId: z
      .string()
      .optional()
      .describe('Filter signals by a specific target entity id'),
    includeDescendants: OptionalBooleanQueryParamSchema.describe(
      'Include signals on descendant assets of targetId',
    ),
    rootEntityKind: ExternalNetworkEntityKindSchema.optional().describe(
      'Kind of the opened asset when includeDescendants is set',
    ),
    stage: z
      .array(SignalStageSchema)
      .optional()
      .describe('Filter by signal stage(s)'),
    status: z
      .array(SignalStatusSchema)
      .optional()
      .describe('Filter by signal status(es)'),
    confirmedVulnerabilityFindingState: z
      .array(ConfirmedVulnerabilityFindingStateSchema)
      .optional(),
    confidence: SurfaceSignalConfidenceFilterSchema,
    origin: z.array(SignalOriginSchema).optional(),
    source: z
      .array(
        z.enum([
          'terra',
          'wiz',
          'upwind',
          'bugcrowd',
          'hackerone',
          'file_import',
        ]),
      )
      .optional()
      .describe('Filter by signal source'),
    assigneeId: z.union([z.string(), z.array(z.string())]).optional(),
    hasExecution: OptionalBooleanQueryParamSchema,
    hasFinding: OptionalBooleanQueryParamSchema,
    hasSecurityTestCase: OptionalBooleanQueryParamSchema,
    importBatchId: OptionalStringArrayQueryParamSchema.describe(
      'Filter signals linked to one or more import batches',
    ),
    cweIds: z.array(z.string()).optional().describe('Filter by CWE IDs'),
    owaspCategory: z
      .array(z.string())
      .optional()
      .describe('Filter by OWASP Top 10 category'),
    createdStartDate: z.coerce.date().optional(),
    createdEndDate: z.coerce.date().optional(),
    testedStartDate: z.coerce.date().optional(),
    testedEndDate: z.coerce.date().optional(),
    rejectedOnly: OptionalBooleanQueryParamSchema,
    includeRejected: OptionalBooleanQueryParamSchema,
    lifecycleState: LifecycleStateFilterSchema.optional().describe(
      'Filter by asset lifecycle state (active, archived, or all)',
    ),
  });

export const ExternalNetworkSignalSchema = z.object({
  id: z.string(),
  category: SignalCategorySchema,
  origin: SignalOriginSchema,
  stage: SignalStageSchema,
  status: SignalStatusSchema.nullable(),
  description: z.string().nullable().optional(),
  confidence: z.number().nullable().optional(),
  cweIds: z.array(z.string()).default([]),
  owaspCategory: z.string().nullable().optional(),
  isActive: z.boolean(),
  targetId: z.string(),
  targetEntityId: z
    .string()
    .nullable()
    .optional()
    .describe('ID of the external network entity the signal relates to'),
  targetEntityKind: ExternalNetworkEntityKindSchema.nullable().optional(),
  targetSummary: z
    .string()
    .nullable()
    .optional()
    .describe('Human-readable label for the target entity'),
  assigneeId: z.string().nullable().optional(),
  lastSmokeTestedAt: z.coerce.date().nullable().optional(),
  validationRejected: z.boolean().nullable().optional(),
  securityTestCaseId: z.string().nullable().optional(),
  smokeTestSummary: z.string().nullable().optional(),
  createdAt: z.coerce.date().optional(),
  linkedFinding: SignalLinkedFindingPreviewSchema.nullish(),
  constituentFindingIds: z.array(z.string()).optional(),
});

export const ExternalNetworkSignalListResponseSchema = z.object({
  data: z.array(ExternalNetworkSignalSchema),
  total: z.number(),
});

export const ExternalNetworkGroupedSignalsByAssetQuerySchema =
  PaginationQueryParamsSchema.extend({
    category: SurfaceSignalCategoryQuerySchema.describe(
      'Filter by signal category',
    ),
    search: z
      .string()
      .optional()
      .describe('Filter grouped signals by search text'),
    assetKind: SignalTargetAssetKindSchema.optional().describe(
      'Filter grouped signals by target asset kind',
    ),
    stage: z
      .array(SignalStageSchema)
      .optional()
      .describe('Filter by signal stage(s)'),
    status: z
      .array(SignalStatusSchema)
      .optional()
      .describe('Filter by signal status(es)'),
    confirmedVulnerabilityFindingState: z
      .array(ConfirmedVulnerabilityFindingStateSchema)
      .optional(),
    origin: z.array(SignalOriginSchema).optional(),
    source: ExternalNetworkSignalListQuerySchema.shape.source,
    importBatchId: ExternalNetworkSignalListQuerySchema.shape.importBatchId,
    createdStartDate: z.coerce.date().optional(),
    createdEndDate: z.coerce.date().optional(),
    cweIds: z.array(z.string()).optional().describe('Filter by CWE IDs'),
    owaspCategory: z
      .array(z.string())
      .optional()
      .describe('Filter by OWASP Top 10 category'),
  });

export const ExternalNetworkSignalsByAssetGroupSchema = z.object({
  assetId: z.string(),
  assetKind: SignalTargetAssetKindSchema,
  assetSummary: z.string().nullable(),
  signalCount: z.int().nonnegative(),
  signals: z.array(ExternalNetworkSignalSchema),
});

export const SurfaceGroupedSignalsByAssetResponseSchema = z.object({
  data: z.array(ExternalNetworkSignalsByAssetGroupSchema),
  total: z.number(),
});

export const ExternalNetworkSignalAssetKindCountsResponseSchema = z.object({
  total: z.int().nonnegative(),
  countsByKind: z.object({
    DOMAIN: z.int().nonnegative(),
    HOST: z.int().nonnegative(),
    NETWORK_SERVICE: z.int().nonnegative(),
    SOFTWARE: z.int().nonnegative(),
    STORAGE_SERVICE: z.int().nonnegative(),
  }),
});

export const ExternalNetworkSignalCategoryByAssetKindRowSchema = z.object({
  category: z.string(),
  assetKind: SignalTargetAssetKindSchema,
  count: z.int().nonnegative(),
});

export const ExternalNetworkSignalCategoryByAssetKindResponseSchema = z.object({
  rows: z.array(ExternalNetworkSignalCategoryByAssetKindRowSchema),
});

export const ExternalNetworkAssetGeoLocationSchema = z.object({
  countryCode: z.string(),
  country: z.string().nullable(),
  count: z.int().nonnegative(),
});

export const ExternalNetworkAssetGeoDistributionResponseSchema = z.object({
  locations: z.array(ExternalNetworkAssetGeoLocationSchema),
  unknownCount: z.int().nonnegative(),
  total: z.int().nonnegative(),
});

export const ExternalNetworkSignalTrendQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(90).optional().prefault(90),
});

export const ExternalNetworkSignalTrendDaySchema = z.object({
  date: z.string(),
  count: z.int().nonnegative(),
});

export const ExternalNetworkSignalTrendResponseSchema = z.object({
  dailyData: z.array(ExternalNetworkSignalTrendDaySchema),
  totalSignals: z.int().nonnegative(),
});

export const ExternalNetworkSignalTrendAggregationRowSchema = z.object({
  _id: z.string(),
  count: z.number(),
});

export const ExternalNetworkSignalTrendAggregationResultSchema = z.array(
  ExternalNetworkSignalTrendAggregationRowSchema,
);

export const SurfaceFindingDtoSchema = FindingListEnrichedDtoSchema.omit({
  applicationId: true,
}).extend({
  targetId: z.string(),
  targetEntityKind: ExternalNetworkEntityKindSchema.nullable().optional(),
});

// ==================== Asset Graph ====================
export const AssetRelationshipTypeSchema = z.enum([
  'WITHIN_NETBLOCK',
  'IS_SUBDOMAIN_OF',
  'RESOLVES_TO',
  'FRONTED_BY',
  'SERVES_VHOST',
  'ORIGIN_OF',
  'RUNS_SERVICE',
  'RUNS_SOFTWARE',
  'HOSTS_APPLICATION',
  'HAS_WEAKNESS',
]);

export const AssetGraphNodeKindSchema = z.union([
  ExternalNetworkEntityKindSchema,
  z.literal('WEAKNESS'),
]);

export const AssetGraphNodeSchema = z.object({
  id: z.string().describe('Entity ID'),
  nodeType: z.enum(['asset', 'cluster', 'weakness']).optional(),
  kind: AssetGraphNodeKindSchema.describe('Entity kind'),
  label: z.string().describe('Display label'),
  properties: z.object({
    scope: ScopeSchema.optional(),
    sourceType: z.string().optional(),
    cidr: z.string().optional(),
    fqdn: z.string().optional(),
    ipAddress: z.string().optional(),
    hostname: z.string().optional(),
    port: z.number().optional(),
    transportProtocol: TransportProtocolSchema.optional(),
    applicationProtocol: ApplicationProtocolSchema.optional(),
    softwareType: SoftwareTypeSchema.optional(),
    vendor: z.string().optional(),
    product: z.string().optional(),
    version: z.string().nullable().optional(),
    cloudProvider: z.string().nullable().optional(),
    cloudAccountRef: z.string().nullable().optional(),
    publicAccess: StorageServicePublicAccessSchema.optional(),
    websiteEnabled: z.boolean().optional(),
    signalsCount: z.number().optional(),
    signalCategory: z.string().optional(),
    signalStage: z.string().optional(),
  }),
  cluster: z
    .object({
      id: z.string(),
      strategy: z.enum(['kind', 'scope', 'risk']),
      childCount: z.number(),
      scope: ScopeSchema.optional(),
      riskBucket: z.enum(['critical', 'high', 'medium', 'low']).optional(),
    })
    .optional(),
});

export const AssetGraphEdgeSchema = z.object({
  id: z.string().describe('Edge identifier'),
  source: z.string().describe('Source node ID'),
  target: z.string().describe('Target node ID'),
  type: AssetRelationshipTypeSchema.describe('Edge label'),
});

export const AssetGraphModeSchema = z.enum([
  'entry-points',
  'neighborhood',
  'full',
  'route',
]);

export const AssetGraphClusterStrategySchema = z.enum([
  'auto',
  'none',
  'kind',
  'scope',
  'risk',
]);

export const AssetGraphQuerySchema = z.object({
  mode: AssetGraphModeSchema.default('entry-points'),
  rootEntityId: z.string().optional(),
  rootEntityKind: ExternalNetworkEntityKindSchema.optional(),
  depth: z.coerce.number().int().min(1).max(3).prefault(1),
  kinds: z.array(ExternalNetworkEntityKindSchema).optional(),
  scopes: z.array(ScopeSchema).optional(),
  riskOnly: z.coerce.boolean().prefault(false),
  limitNodes: z.coerce.number().int().min(1).max(3000).prefault(1500),
  limitEdgesPerNode: z.coerce.number().int().min(1).max(200).prefault(50),
  clusterStrategy: AssetGraphClusterStrategySchema.default('auto'),
  clusterThreshold: z.coerce.number().int().min(20).max(2000).prefault(180),
  expandedClusterIds: z.array(z.string()).optional(),
});

export const AssetGraphResponseSchema = z.object({
  nodes: z.array(AssetGraphNodeSchema),
  edges: z.array(AssetGraphEdgeSchema),
  truncated: z.boolean(),
  totalCount: z.number(),
});

export const ExternalNetworkAssetEntitySummarySchema = z.object({
  nodeId: z.string(),
  entityKind: ExternalNetworkEntityKindSchema,
  displayName: z.string(),
});

export const SearchExternalNetworkEntitiesQuerySchema = z.object({
  q: z.string().min(2).max(200),
  limit: z.coerce.number().min(1).max(25).optional(),
  skip: z.coerce.number().min(0).max(100_000).optional(),
});

export const SearchExternalNetworkEntitiesResponseSchema = z.object({
  entities: z.array(ExternalNetworkAssetEntitySummarySchema),
  total: z.number(),
});

// ==================== Surface Findings ====================
export const SurfaceFindingsListQuerySchema =
  PaginationQueryParamsSchema.extend({
    severity: z.string().optional().describe('Filter by severity'),
    severities: z
      .array(FindingSeveritySchema)
      .optional()
      .describe('Filter by one or more severities'),
    status: z.string().optional().describe('Filter by status'),
    statuses: z
      .array(FindingStatusSchema)
      .optional()
      .describe('Filter by one or more statuses'),
    assetSearch: z
      .string()
      .max(200)
      .optional()
      .describe('Filter findings linked to assets matching this search term'),
    assetIds: OptionalStringArrayQueryParamSchema.describe(
      'Filter findings linked to these asset ids',
    ),
    includeDescendants: OptionalBooleanQueryParamSchema.describe(
      'Include findings on descendant assets of assetIds',
    ),
    rootEntityKind: ExternalNetworkEntityKindSchema.optional().describe(
      'Kind of the opened asset when includeDescendants is set',
    ),
    cweIds: z.array(z.string()).optional().describe('Filter by CWE IDs'),
    owaspCategory: z
      .array(z.string())
      .optional()
      .describe('Filter by OWASP Top 10 category'),
    signalId: z
      .string()
      .optional()
      .describe('Filter findings by a specific signal id'),
    includeUnpublished: OptionalBooleanQueryParamSchema.describe(
      'Include unpublished findings (requires Execute Tests permission)',
    ),
    infraImpact: z
      .string()
      .optional()
      .describe('Filter by closed infra impact class'),
    category: z.string().optional().describe('Filter by finding category'),
  });

export const SurfaceFindingsListResponseSchema = z.object({
  data: z.array(SurfaceFindingDtoSchema),
  total: z.number(),
});

export const SurfaceFindingsCountQuerySchema =
  SurfaceFindingsListQuerySchema.omit({
    skip: true,
    limit: true,
  });

export const SurfaceFindingsCountResponseSchema = z.object({
  count: z.number(),
});

export const ExternalNetworkSignalsCountQuerySchema =
  ExternalNetworkSignalListQuerySchema.omit({
    skip: true,
    limit: true,
  });

export const ExternalNetworkSignalsCountResponseSchema = z.object({
  count: z.number(),
});

export const ExternalNetworkFullReportBodySchema = z.object({
  findingIds: z.array(z.string()).min(1),
  title: z.string().min(1).optional(),
  jobId: z.string().uuid().optional(),
});

export const ExternalNetworkFullReportResponseSchema = z.object({
  reportId: z.string(),
  title: z.string(),
  findingIds: z.array(z.string()),
});

// ==================== Scan Activity Logs ====================
export const ScanActivityLogEntrySchema = z.object({
  message: z.string(),
  stage: z.string().optional(),
  status: z.string().optional(),
  ts: z.number(),
  workerIndex: z.number().optional(),
  clusterLabel: z.string().optional(),
  signalsCreated: z.number().optional(),
});

export const UpdateNetblockSchema = z.object({
  scope: ScopeSchema.optional(),
});

export const UpdateDomainSchema = z.object({
  scope: ScopeSchema.optional(),
});

export const GuardrailRuleSchema = z.object({
  id: z.string(),
  rule: z.string(),
});

export const GuardrailValidationErrorSchema = z.object({
  field: z.string(),
  fieldType: z.enum(['description', 'rule']),
  ruleIndex: z.number().optional(),
  issues: z.array(z.string()),
  originalContent: z.string(),
});

export const NoteValidationErrorSchema = z.object({
  field: z.string(),
  fieldType: z.literal('content'),
  issues: z.array(z.string()),
  originalContent: z.string(),
});

export const NetworkGuardrailSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  surfaceId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  category: GuardrailCategorySchema,
  enabled: z.boolean().default(true),
  rules: z.array(GuardrailRuleSchema),
  action: GuardrailActionSchema.default('WARN'),
  isDefault: z.boolean().default(false),
  triggerCount: z.number().default(0),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const NetworkGuardrailCreateSchema = NetworkGuardrailSchema.omit({
  id: true,
  tenantId: true,
  surfaceId: true,
  createdAt: true,
  updatedAt: true,
});

export const NetworkGuardrailUpdateSchema =
  NetworkGuardrailCreateSchema.partial();

export const NetworkGuardrailsReturnSchema = z.object({
  guardrails: z.array(NetworkGuardrailSchema),
});

export const NetworkGuardrailValidationResponseSchema = z.object({
  success: z.boolean(),
  data: NetworkGuardrailSchema.nullable(),
  validationErrors: z.array(GuardrailValidationErrorSchema).nullable(),
  message: z.string().optional(),
});

export const NetworkNoteSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  surfaceId: z.string(),
  title: z.string(),
  content: z.string(),
  category: InstructionCategorySchema.nullable(),
  applyMode: TestingInstructionApplyModeSchema.default('ALWAYS'),
  pipelines: TestingInstructionPipelineSchema.array().default([]),
  endpointGlobs: z.array(z.string()).default([]),
  description: z.string().nullable().default(null),
  createdBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

const NetworkNoteCreateBaseSchema = NetworkNoteSchema.omit({
  id: true,
  tenantId: true,
  surfaceId: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
});

export const NetworkNoteCreateSchema = NetworkNoteCreateBaseSchema.superRefine(
  (values, context) => {
    if (values.applyMode === 'AUTO_ATTACHED') {
      const hasGlob = values.endpointGlobs.some(
        (glob) => glob.trim().length > 0,
      );

      if (!hasGlob) {
        context.addIssue({
          code: 'custom',
          path: ['endpointGlobs'],
          message:
            'At least one endpoint glob is required for auto-attached mode',
        });
      }
    }

    if (values.applyMode === 'AGENT_REQUESTED') {
      if (!values.description?.trim()) {
        context.addIssue({
          code: 'custom',
          path: ['description'],
          message: 'Description is required for agent-requested mode',
        });
      }
    }
  },
);

export const NetworkNoteUpdateSchema = NetworkNoteCreateBaseSchema.partial();

export const NetworkNoteWithUserSchema = NetworkNoteSchema.extend({
  createdByUser: z
    .object({
      id: z.string(),
      name: z.string().nullable(),
    })
    .nullable(),
});

export const NetworkNotesReturnSchema = z.object({
  notes: z.array(NetworkNoteWithUserSchema),
});

export const NetworkNoteValidationResponseSchema = z.object({
  success: z.boolean(),
  data: NetworkNoteSchema.nullable(),
  validationErrors: z.array(NoteValidationErrorSchema).nullable(),
  message: z.string().optional(),
});

export const CloudAccountPolicyProviderSchema = z.enum([
  'aws',
  'gcp',
  'azure',
  'cloudflare',
]);

export const CloudAccountPolicyStatusSchema = z.enum([
  'ACTIVE',
  'SYNC_ERROR',
  'DISCONNECTED',
]);

export const CloudAccountAutoScopeSchema = z.enum([
  'IN_SCOPE',
  'PENDING',
  'OUT_OF_SCOPE',
]);

export const CloudAccountPolicyDtoSchema = z.object({
  accountId: z.string(),
  provider: CloudAccountPolicyProviderSchema,
  accountRef: z.string(),
  displayName: z.string(),
  status: CloudAccountPolicyStatusSchema,
  lastSyncedAt: z.coerce.date().nullable(),
  lastError: z.string().nullable(),
  enabled: z.boolean(),
  autoScope: CloudAccountAutoScopeSchema,
});

export const ListCloudAccountPoliciesResponseSchema = z.object({
  data: z.array(CloudAccountPolicyDtoSchema),
});

export const UpdateCloudAccountPolicyBodySchema = z.object({
  autoScope: CloudAccountAutoScopeSchema,
  enabled: z.boolean().optional(),
});

export const CloudAccountPolicyPathParamsSchema = z.object({
  surfaceId: z.string(),
  accountId: z.string(),
});

export {
  StorageServiceDtoSchema,
  StorageServiceListQuerySchema,
  StorageServiceListResponseSchema,
  StorageServicePublicAccessSchema,
} from './storage-service.schema';
